import { Chip, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { formatTranslation } from 'intl';
import { DocumentationTranslationKeys } from 'intl/keys';
import { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { formatVersionNumber } from 'shared/domain/documentation/versionState';
import { Color } from 'shared/utils/colors';

const pillSx: SxProps<Theme> = {
  height: 22,
  fontWeight: 600,
};

const pillOnDarkSx: SxProps<Theme> = {
  ...pillSx,
  color: Color.white,
  borderColor: Color.whiteOverlay60,
};

const noCurrentVersionSx: SxProps<Theme> = {
  color: Color.textMuted,
  whiteSpace: 'nowrap',
};

const noCurrentVersionOnDarkSx: SxProps<Theme> = {
  ...pillSx,
  color: Color.white,
  backgroundColor: Color.grey600,
};

interface Props {
  number: number | null | undefined;
  /** In the dark gallery top bar: light pill, with the "Current version number" tooltip. */
  onDark?: boolean;
}

/** A documentation's current version as a `vN` pill, or "No current version" without one (PT-7117 mockup). */
export function CurrentVersionNumber({ number, onDark = false }: Props): ReactElement {
  const intl = useIntl();
  const label = formatVersionNumber(number);

  if (!label) {
    const noCurrentVersion = formatTranslation(intl, DocumentationTranslationKeys.noCurrentVersion);
    return onDark ? (
      <Chip size='small' label={noCurrentVersion} sx={noCurrentVersionOnDarkSx} data-qa='documentation-no-current-version' />
    ) : (
      <Typography variant='body2' component='span' sx={noCurrentVersionSx} data-qa='documentation-no-current-version'>
        {noCurrentVersion}
      </Typography>
    );
  }

  if (!onDark) {
    return <Chip size='small' variant='outlined' label={label} sx={pillSx} data-qa='documentation-current-version-number' />;
  }

  return (
    <Tooltip title={formatTranslation(intl, DocumentationTranslationKeys.currentVersionNumberLabel)}>
      <Chip size='small' variant='outlined' label={label} sx={pillOnDarkSx} data-qa='documentation-current-version-number' />
    </Tooltip>
  );
}
